// src/components/exam-player/renderers/McqSingleRenderer.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export default function McqSingleRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  questionNumber,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const options = Array.isArray(item?.body?.options) ? item.body.options : [];
  const layout = item?.body?.layout || "top-bottom";

  // Passage (اختیاری)
  const passageHtml = item?.body?.text?.fa ?? item?.body?.text?.en ?? ""; // اگر خالی بود، یعنی Passage نداریم

  // مقدار اولیه (پیش‌پرکردن در بازدید مجدد)
  const initialSelected = useMemo(() => {
    if (!initialAnswers) return null;
    if (typeof initialAnswers === "string") return initialAnswers;
    if (typeof initialAnswers?.selected === "string")
      return initialAnswers.selected;
    return null;
  }, [initialAnswers]);

  const [selected, setSelected] = useState(initialSelected);

  useEffect(() => {
    setSelected(initialSelected);
  }, [initialSelected]);

  const groupName = `mcq-${item?._id || questionNumber || "q"}`;

  const handleChange = (key) => {
    setSelected(key);
    onAnswerChange?.({ selected: key }); // ← فرمت پاسخ ثابت ماند
  };

  // بلوک سؤال/گزینه‌ها (همون رفتار قبلی)
  const QuestionBlock = () => (
    <>
      <div className="my-1 leading-3">
        {!passageHtml && questionNumber}
      </div>  
      <div className="prose prose-sm max-w-none mb-2 leading-3">
        <div
          dangerouslySetInnerHTML={{
            __html: item?.stem?.fa || item?.stem?.en || t("preview.no_stem"),
          }}
        />
        {(item.body?.passage?.fa || item.body?.passage?.en) && (
          <div
            dangerouslySetInnerHTML={{
              __html: item.body?.passage?.fa || item.body?.passage?.en,
            }}
          />
        )}
      </div>
      <ul className="space-y-2">
        {/* اگر نمی‌خوای شماره سؤال اینجا باشه، این خط رو بردار */}

        {options.map((opt, idx) => {
          const key = opt?.key ?? String(idx);
          const label =
            opt?.label?.fa ||
            opt?.label?.en ||
            t("preview.option") + ` ${idx + 1}`;
          return (
            <li key={key} className="flex items-start gap-2">
              <input
                type="radio"
                name={groupName}
                className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500"
                checked={selected === key}
                onChange={() => handleChange(key)}
                aria-label={label}
              />
              <label
                className={`text-sm cursor-pointer select-none ${
                  selected === key ? "font-medium" : ""
                }`}
                onClick={() => handleChange(key)}
              >
                {label}
              </label>
            </li>
          );
        })}
      </ul>
    </>
  );

  // اگر Passage نداریم → همان رفتار قبلی
  if (!passageHtml) {
    return <QuestionBlock />;
  }

  // با Passage و بر اساس layout
  if (layout === "left-right" || layout === "right-left") {
    const leftIsPassage = layout === "left-right"; // طبق خواسته: left-right یعنی Passage چپ، سؤال راست
    return (
      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
        dir={isRtl ? "rtl" : "ltr"}
      >
        {/* ستون چپ */}
        <div className="prose max-w-none md:sticky md:top-4 md:max-h-[calc(100vh-220px)] md:overflow-y-auto">
          {leftIsPassage ? (
            <div
              className="p-3 border rounded-lg bg-gray-50"
              dangerouslySetInnerHTML={{ __html: passageHtml }}
            />
          ) : (
            <QuestionBlock />
          )}
        </div>

        {/* ستون راست */}
        <div className="prose max-w-none">
          {leftIsPassage ? (
            <QuestionBlock />
          ) : (
            <div
              className="p-3 border rounded-lg bg-gray-50"
              dangerouslySetInnerHTML={{ __html: passageHtml }}
            />
          )}
        </div>
      </div>
    );
  }

  // top-bottom: Passage بالا، سؤال پایین
  if (layout === "top-bottom") {
    return (
      <div dir={isRtl ? "rtl" : "ltr"}>
        <div
          className="prose max-w-none p-3 border rounded-lg bg-gray-50 mb-4"
          dangerouslySetInnerHTML={{ __html: passageHtml }}
        />
        <QuestionBlock />
      </div>
    );
  }

  // bottom-top: سؤال بالا، Passage پایین
  if (layout === "bottom-top") {
    return (
      <div dir={isRtl ? "rtl" : "ltr"}>
        <QuestionBlock />
        <div
          className="prose max-w-none p-3 border rounded-lg bg-gray-50 mt-4"
          dangerouslySetInnerHTML={{ __html: passageHtml }}
        />
      </div>
    );
  }

  // پیش‌فرض fallback (برای امنیت)
  return (
    <div dir={isRtl ? "rtl" : "ltr"}>
      <QuestionBlock />
    </div>
  );
}
